<script setup>
import { ref, computed, defineProps, inject } from 'vue';
import LineChart from '@/components/result/MLSRD-2026/LineChart.vue';

const schoolData = inject('schoolData');

// 이번 시험 연도(classList의 exam_year) 기준 최근 3년. 예: 2026 → [2024, 2025, 2026]
const currentYear = computed(() => {
  const year = Number(schoolData.value?.classList?.[0]?.exam_year);
  return Number.isFinite(year) && year > 0 ? year : new Date().getFullYear();
});
const years = computed(() => [currentYear.value - 2, currentYear.value - 1, currentYear.value]);
const isYear = (item, year) => Number(item.exam_year) === year;

// 학년별 점수 범위 데이터
const scoreData = {
    literacy: {
        '4학년': {levels:[{level:1,range:'1000~1226'},{level:2,range:'1227~1353'},{level:3,range:'1354~1526'},{level:4,range:'1527~1700'},],},
        '6학년': {levels:[{level:1,range:'1050~1320'},{level:2,range:'1321~1432'},{level:3,range:'1433~1594'},{level:4,range:'1595~1800'},],},
        '중2학년': {levels:[{level:1,range:'1100~1412'},{level:2,range:'1413~1529'},{level:3,range:'1530~1665'},{level:4,range:'1666~1900'},],},
        '고1학년': {levels:[{level:1,range:'1150~1534'},{level:2,range:'1535~1622'},{level:3,range:'1623~1739'},{level:4,range:'1740~2000'},],},
    },
    numeracy: {
        '4학년': {levels:[{level:1,range:'1000~1225'},{level:2,range:'1226~1341'},{level:3,range:'1342~1449'},{level:4,range:'1450~1700'},],},
        '6학년': {levels:[{level:1,range:'1050~1294'},{level:2,range:'1295~1387'},{level:3,range:'1388~1541'},{level:4,range:'1542~1800'},],},
        '중2학년': {levels:[{level:1,range:'1100~1387'},{level:2,range:'1388~1500'},{level:3,range:'1501~1620'},{level:4,range:'1621~1900'},],},
        '고1학년': {levels:[{level:1,range:'1150~1435'},{level:2,range:'1436~1573'},{level:3,range:'1574~1696'},{level:4,range:'1697~2000'},],},
    },
};

// API 데이터 기반으로 차트 데이터 생성
const generateChartData = (gradeCode, subject) => {
  
  if (!schoolData.value) {
    return [
      { label: '표집학교', scores: [null, null, null] },
      { label: '우리학교', scores: [null, null, null] }
    ];
  }

  const { totalList, classList, beforeScoreList, beforeTotalScoreList, currentScoreList } = schoolData.value;
  
  if (!totalList || !classList) {
    return [
      { label: '표집학교', scores: [null, null, null] },
      { label: '우리학교', scores: [null, null, null] }
    ];
  }
  
  const scoreKey = subject === 'literacy' ? 'korscore' : 'matscore';
  const [year1, year2, year3] = years.value; // 2년 전, 1년 전, 이번 시험

  // 해당 학년의 이번 시험 데이터
  const currentGradeData = totalList.find(item => item.grade_code === gradeCode);

  // 이전 연도 점수 찾기 (문자열/숫자 연도 모두 처리)
  const findScore = (list, year) => {
    const found = list?.find(item => item.grade_code === gradeCode && isYear(item, year));
    const value = found ? Number(found[scoreKey]) : NaN;
    return Number.isFinite(value) ? Math.round(value) : null;
  };

  // 표집학교 점수
  const currentTotalScore = currentGradeData ? currentGradeData[scoreKey] : null;
  const totalScoreYear1 = findScore(beforeTotalScoreList, year1);
  const totalScoreYear2 = findScore(beforeTotalScoreList, year2);
  
  // 우리학교 점수: 모든 연도를 학교 집계(tb_exam_result_mlsrd_school)에서 그대로 쓴다 (화면에서 계산하지 않음)
  const ourScoreYear1 = findScore(beforeScoreList, year1);
  const ourScoreYear2 = findScore(beforeScoreList, year2);
  const currentOurScore = findScore(currentScoreList, year3);

  const result = [
    {
      label: '표집학교',
      scores: [totalScoreYear1, totalScoreYear2, currentTotalScore]
    },
    {
      label: '우리학교',
      scores: [ourScoreYear1, ourScoreYear2, currentOurScore]
    }
  ];
  
  return result;
};

// 첫 번째 학년 문해력 데이터 (초등: 4학년, 중등: 2학년, 고등: 1학년)
const data1 = computed(() => {
  const result = generateChartData(grade.value, 'literacy');
  return result;
});

// 첫 번째 학년 수리력 데이터  
const data2 = computed(() => {
  const result = generateChartData(grade.value, 'numeracy');
  return result;
});

// 두 번째 학년 문해력 데이터 (초등학교에서 두 번째 학년이 있을 때)
const data3 = computed(() => {
  if (hasSecondGrade.value) {
    const result = generateChartData(secondGrade.value, 'literacy');
    return result;
  }
  return [
    { label: '표집학교', scores: [null, null, null] },
    { label: '우리학교', scores: [null, null, null] }
  ];
});

// 두 번째 학년 수리력 데이터 (초등학교에서 두 번째 학년이 있을 때)
const data4 = computed(() => {
  if (hasSecondGrade.value) {
    const result = generateChartData(secondGrade.value, 'numeracy');
    return result;
  }
  return [
    { label: '표집학교', scores: [null, null, null] },
    { label: '우리학교', scores: [null, null, null] }
  ];
});

// 응시현황 텍스트 생성
const getAttendanceText = (gradeCode) => {
  if (!schoolData.value?.classList) return '';
  
  const gradeClasses = schoolData.value.classList.filter(item => item.grade_code === gradeCode);
  if (gradeClasses.length === 0) return '';

  // 이번 시험 응시자 수: 학교 집계(tb_exam_result_mlsrd_school)의 kor_cnt / mat_cnt
  const currentData = schoolData.value.currentScoreList?.find(item => item.grade_code === gradeCode);
  const currentLiteracyCount = currentData?.kor_cnt || 0;
  const currentNumeracyCount = currentData?.mat_cnt || 0;

  // beforeScoreList에서 해당 학년의 그래프 기간(이번 시험 전 2년) 데이터만 사용
  const [year1, year2, currentYear] = years.value;
  const beforeDataList = schoolData.value.beforeScoreList?.filter(item =>
    item.grade_code === gradeCode && (isYear(item, year1) || isYear(item, year2))
  ) || [];
  
  let attendanceText = '';
  
  // 년도별로 정렬해서 표시 (문자열을 숫자로 변환)
  const sortedData = beforeDataList.sort((a, b) => parseInt(a.exam_year) - parseInt(b.exam_year));
  
  sortedData.forEach(data => {
    if (attendanceText) attendanceText += ' ';
    const literacyCount = data.kor_cnt || 0;
    const numeracyCount = data.mat_cnt || 0;
    attendanceText += `(${data.exam_year}년) 문해력 ${literacyCount}명, 수리력 ${numeracyCount}명`;
  });
  
  // 현재 년도 데이터 추가
  if (attendanceText) attendanceText += ' ';
  attendanceText += `(${currentYear}년) 문해력 ${currentLiteracyCount}명, 수리력 ${currentNumeracyCount}명`;
  
  return `응시현황 : ${attendanceText}`;
};

// 학년 및 과목에 따른 기본 Y축 범위 계산
const getDefaultYAxisRange = (gradeCode, subject) => {
  const gradeMap = {
    '4': '4학년',
    '6': '6학년',
    '2': '중2학년',
    '1': '고1학년'
  };
  
  const gradeName = gradeMap[gradeCode] || '4학년';
  const subjectData = scoreData[subject]?.[gradeName];
  
  if (subjectData && subjectData.levels) {
    const minRange = parseInt(subjectData.levels[0].range.split('~')[0]);
    const maxRange = parseInt(subjectData.levels[subjectData.levels.length - 1].range.split('~')[1]);
    return {
      min: minRange,
      max: maxRange,
      interval: Math.max(Math.floor((maxRange - minRange) / 10 / 10) * 10, 20)
    };
  }
  
  return { min: 1400, max: 1700, interval: 30 };
};

// Y축 범위 계산 함수
const getYAxisRange = (data, gradeCode, subject) => {
  
  if (!data || !Array.isArray(data)) {
    return getDefaultYAxisRange(gradeCode, subject);
  }
  
  const allScores = data.flatMap(item => {
    if (!item || !Array.isArray(item.scores)) return [];
    return item.scores.filter(score => score !== null && typeof score === 'number');
  });
  
  
  if (allScores.length === 0) {
    return getDefaultYAxisRange(gradeCode, subject);
  }
  
  const minScore = Math.min(...allScores);
  const maxScore = Math.max(...allScores);
  
  
  // scoreData 기반의 기본 범위 가져오기
  const defaultRange = getDefaultYAxisRange(gradeCode, subject);
  
  // 실제 데이터 범위와 기본 범위를 비교해서 더 넓은 범위 사용
  const finalMin = Math.min(defaultRange.min, minScore - 50);
  const finalMax = Math.max(defaultRange.max, maxScore + 50);
  
  // 20 단위로 정리
  const min = Math.floor(finalMin / 20) * 20;
  const max = Math.ceil(finalMax / 20) * 20;
  const interval = Math.max(Math.floor((max - min) / 8 / 10) * 10, 20);
  
  const result = { min, max, interval };
  
  return result;
};

// 각 차트의 Y축 범위
const chart1Range = computed(() => {
  return getYAxisRange(data1.value, grade.value, 'literacy');
});
const chart2Range = computed(() => {
  return getYAxisRange(data2.value, grade.value, 'numeracy');
});
const chart3Range = computed(() => {
  return getYAxisRange(data3.value, secondGrade.value, 'literacy');
});
const chart4Range = computed(() => {
  return getYAxisRange(data4.value, secondGrade.value, 'numeracy');
});

const region = "seoul";

const props = defineProps({
    schoolType: {
        type: String, 
        required: true,
        validator: (value) => ['elementary', 'middle', 'high'].includes(value),
    }
});

// 실제 데이터에서 grades 정보 추출
const availableGrades = computed(() => {
    if (schoolData.value && schoolData.value.classList && schoolData.value.classList.length > 0) {
        const gradeArray = [...new Set(schoolData.value.classList.map(item => item.grade_code))].filter(Boolean);
        return gradeArray.sort();
    }
    // 기본값
    if (props.schoolType === 'elementary') {
        return ['4', '6']; 
    } else if (props.schoolType === 'middle') {
        return ['2']; 
    } else if (props.schoolType === 'high') {
        return ['1']; 
    }
    return []; 
});

const grade = computed(() => {
    return availableGrades.value[0] || '';
});

const secondGrade = computed(() => {
    return availableGrades.value[1] || '';
});

// 초등학교에서 6학년 데이터가 실제로 있는지 확인
const hasSecondGrade = computed(() => {
    return props.schoolType === 'elementary' && availableGrades.value.length > 1 && secondGrade.value;
});
</script>

<template>
    <div class="slide-inner">
        <div class="content">    
            <div class="page-title">학교 평균 점수 변화 추이</div>
            <p class="caution pt-5 pb-[5px]">최근 3년간 본 진단검사에 응시한 학교의 해당 학년 평균 점수 변화 추이를 보여줍니다.</p>
            <p v-if="years.includes(2023)" class="caution pb-5">2023년 점수는 2024년 기준척도로 변환된 점수입니다.</p>
            <div class="card-wrap">
                <div class="card pb-5">
                    <h4 class="title-box literacy" :class="[region, `grade`+grade]">
                        <template v-if="props.schoolType === 'elementary'">
                        초등학교 {{ grade }}학년
                        </template>
                        <template v-else-if="props.schoolType === 'middle'">
                        중학교 {{ grade }}학년
                        </template>
                        <template v-else-if="props.schoolType === 'high'">
                        고등학교 {{ grade }}학년
                        </template>
                        <template v-else>
                        {{ grade }}학년
                        </template>
                    </h4> 
                    <p class="caution pt-5 pb-5">{{ getAttendanceText(grade) }}</p>
                    <n-flex :wrap="false" :size="30">
                        <LineChart            
                            :years="years"
                            :data="data1"
                            :y-axis-min="chart1Range.min"
                            :y-axis-max="chart1Range.max"
                            :y-axis-interval="chart1Range.interval"
                            chart-type="literacy"   
                            chart-height="300px"
                        />
                        <LineChart            
                            :years="years"
                            :data="data2"
                            :y-axis-min="chart2Range.min"
                            :y-axis-max="chart2Range.max"
                            :y-axis-interval="chart2Range.interval"
                            chart-type="numeracy"  
                            chart-height="300px"
                        />
                    </n-flex>
                </div>       

                <template v-if="hasSecondGrade">
                    <div class="card pt-10">
                        <h4 class="title-box numeracy grade6" :class="region">초등학교 {{ secondGrade }}학년</h4>
                        <p class="caution pt-5 pb-5">{{ getAttendanceText(secondGrade) }}</p>
                        <n-flex :wrap="false" :size="30">
                            <LineChart            
                                :years="years"
                                :data="data3"
                                :y-axis-min="chart3Range.min"
                                :y-axis-max="chart3Range.max"
                                :y-axis-interval="chart3Range.interval"
                                chart-type="literacy"   
                                chart-height="300px"
                            />
                            <LineChart            
                                :years="years"
                                :data="data4"
                                :y-axis-min="chart4Range.min"
                                :y-axis-max="chart4Range.max"
                                :y-axis-interval="chart4Range.interval"
                                chart-type="numeracy"  
                                chart-height="300px"
                            />
                        </n-flex>
                    </div>        
                </template>                 
            </div>
        </div> 
    </div> 
</template>
